<template>
  <div class="relative w-full flex flex-col min-h-0"
  :class="loadingAdvertisements ? 'overflow-y-hidden' : 'overflow-y-scroll'" 
  style="height: calc(100vh - 200px); scrollbar-gutter: stable">
    <!-- Pinned section -->
    <div v-if="pinnedGroups.length > 0 && !loadingPinnedHooks" class="w-full flex flex-col border-b border-border-normal min-h-0 flex-shrink-0">
      <div class="flex flex-row justify-between px-4 py-3 items-center" style="height: 52px;">
        <div class="flex flex-row items-center gap-2">
          <PinOutlineIcon :width="20" :height="20" class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Pinned Hooks
          </BaseText>
        </div>
        <div class="flex flex-row items-center gap-2">
          <transition>
            <button v-if="showOptions" class="flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-background-normal transition-colors"
            @click.stop="clearPins">
              <UnpinIcon :width="20" :height="20" class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted">
                Clear Pins
              </BaseText>
            </button>
          </transition>
          <button class="w-8 h-8 flex items-center justify-center rounded-md transition-colors hover:bg-neutral-25" 
          @click="showOptions = !showOptions">
            <ChevronIcon class="text-icon-normal transform transition-transform" :style="{ transform: showOptions ? 'scaleY(-1)' : 'scaleY(1)' }" />
          </button>
        </div>
      </div>
      <div class="collapsible-pinned-hooks" :style="{ height: showOptions ? `${pinnedGroupsHeight}px` : '0px' }">
        <div class="w-full px-3" :style="{ height: `${pinnedGroupsHeight}px` }">
          <HookRowV2
            v-for="group in pinnedGroups"
            :key="`pinned-${group.hookKey}`"
            class="fade-in"
            :group="group"
            :sort="sort"
            :is-pinned="true"
            @see-ads="onSeeAds"
            @pin="onPin(group)"
            @unpin="onUnpin(group)"
            @open-ad="onOpenSingleAd"
            @hover-thumbnail="handleHookThumbnailHover"
          />
        </div>
      </div>
    </div>

    <!-- Unpinned groups -->
    <div class="flex flex-col px-3 py-3 flex-grow min-h-0">
      <HookRowV2
        v-for="(group, index) in unpinnedGroups"
        :key="`unpinned-${group.hookKey}-${index}`"
        class="fade-in"
        :group="group"
        :sort="sort"
        :is-pinned="false"
        :is-search="isSearch"
        :text-search="textSearch"
        @see-ads="onSeeAds"
        @pin="onPin(group)"
        @unpin="onUnpin(group)"
        @open-ad="onOpenSingleAd"
        @hover-thumbnail="handleHookThumbnailHover"
      />
      <HooksListLoadingState v-if="loadingAdvertisements" />

      <!-- No Hooks State -->
      <div v-else-if="!textSearch.length && !unpinnedGroups.length && !pinnedGroups.length" class="flex flex-col items-center mt-20 fade-in">
        <BaseText type="heading" size="md" class="text-text-muted">
          No hooks yet
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal mt-2 text-center">
          We haven't found any transcribed hooks for this brand yet.
        </BaseText>
      </div>

      <!-- No Search Results State -->
      <div v-else-if="textSearch.length && !unpinnedGroups.length" class="flex flex-col items-center mt-20 fade-in">
        <NoSearchResultsIcon light-mode />
        <BaseText type="label" size="md" class="text-text-muted mt-1">
          No hooks matching your search
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal mt-1.5 text-center">
          Try broadening your query
        </BaseText>
        <button class="secondary-btn flex items-center gap-1.5 py-1.5 pl-1.5 pr-2.5 rounded-md mt-5"
        @click="$emit('clear-search')">
          <ClearFiltersIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Clear search
          </BaseText>
        </button>
      </div>

      <!-- Infinite scroll loader (unpinned list) -->
      <infinite-loading
        v-if="!loadingAdvertisements && unpinnedGroups?.length"
        :identifier="infiniteId"
        @infinite="fetchMore"
      >
        <div slot="spinner">
          <BaseLoadingLogo :margin="2" />
        </div>
        <div slot="no-more" />
        <div slot="no-results" />
      </infinite-loading>
    </div>

    <!-- Multi-ad drawer -->
    <HookAdsDrawer
      v-if="showMultiDrawer && selectedHookGroup"
      :group="selectedHookGroup"
      :brand-id="$route.params.id"
      :filter-opts="hookFilterOpts"
      :is-parent-drawer="showAdvertisementDetailsDrawer && !trialExpired"
      @close="onCloseMultiDrawer"
      @open-ad="onOpenSingleAd"
    />

    <!-- Single ad details drawer -->
    <AdvertisementDetailsDrawer
      v-if="showAdvertisementDetailsDrawer && !trialExpired"
      :all-ads.sync="advertisements"
      :discovery="true"
      :advertisement="selectedAdvertisement"
      :shared-board="sharedBoard"
      :shared-brief="sharedBrief"
      :loading="loading"
      :is-child-drawer="showMultiDrawer && selectedHookGroup"
      @getMoreAds="$emit('getMoreAds')"
      @close="closeModal"
      @save="$emit('reload')"
    />

    <transition>
      <BasePopupVideoPreview 
        v-if="hookPreview.video"
        class="fixed z-50 transform -translate-y-1/2"
        :style="{ top: hookPreview.y + 'px', left: hookPreview.x + 'px' }"
        :video-src="hookPreview.video"
        :attempt-manual-play="attemptManualPreviewPlay"
      />
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import InfiniteLoading from 'vue-infinite-loading'
import ForeplayAPI from '@/api/foreplayServer'
import { pinHook, unpinHook, clearHookPins } from '@/api/services/PinnedHooksService'

// Components
import AdvertisementDetailsDrawer from '@/components/advertisements/AdvertisementDetailsDrawer.vue'
import HookRowV2 from '@/components/spyderV2/hooks/HookRowV2.vue'
import HookAdsDrawer from '@/components/spyderV2/hooks/HookAdsDrawer.vue'
import HooksListLoadingState from '@/components/spyderV2/hooks/HooksListLoadingState.vue'

// Icons
import PinOutlineIcon from '@/components/globals/Icons/SpyderV2Icons/PinOutlineIcon.vue'
import UnpinIcon from '@/components/globals/Icons/SpyderV2Icons/UnpinIcon.vue'
import ChevronIcon from '@/components/globals/Icons/ChevronIcon.vue'
import NoSearchResultsIcon from '@/components/globals/Icons/SidebarIcons/NoSearchResultsIcon.vue'
import ClearFiltersIcon from '@/components/globals/Icons/FilterIcons/ClearFiltersIcon.vue'

const ROW_HEIGHT = 52 // px

export default {
  name: 'AdvertisementHooksList',
  components: {
    AdvertisementDetailsDrawer,
    HookRowV2,
    HookAdsDrawer,
    HooksListLoadingState,
    PinOutlineIcon,
    UnpinIcon,
    ChevronIcon,
    NoSearchResultsIcon,
    ClearFiltersIcon,
    InfiniteLoading
  },
  props: {
    filters: {
      type: Object,
      required: true
    },
    textSearch: {
      type: String,
      required: true
    },
    liveStatus: {
      type: Array,
      required: true
    },
    sort: {
      type: String,
      default: 'longest_running'
    }
  },
  data () {
    return {
      selectedAdvertisement: {},
      showAdvertisementDetailsDrawer: false,
      showOptions: true,
      infiniteId: +new Date(),
      advertisements: [],
      groups: [],
      pinnedGroups: [],
      isSearch: false,
      // Offset cursor from /brands/hooks (null = no more pages).
      nextPage: null,
      loadingAdvertisements: true,
      loadingPinnedHooks: false,
      selectedHookGroup: null,
      showMultiDrawer: false,
      hookPreview: { video: null, x: 0, y: 0 },
      attemptManualPreviewPlay: false
    }
  },
  computed: {
    ...mapState('AdvertisementsModule', ['adToOpen']),
    ...mapGetters('AuthModule', [
      'getUserFreeTrialDate',
      'isFreeTier',
      'getTeam',
      'getUser'
    ]),
    filtersKey () {
      return JSON.stringify(this.filters)
    },
    sortKey () {
      return this.sort
    },
    // Same filter/sort opts used for the hook groups request, shared with the
    // creatives drawer so it fetches ads under identical constraints. Excludes
    // hook-list-only fields (excludePinned, hookSearch).
    hookFilterOpts () {
      return this.buildFetchOpts()
    },
    trialExpired () {
      return (
        this.isFreeTier &&
        Date.now() > this.getUserFreeTrialDate + 6.048e8 &&
        !this.getTeam &&
        !this.discovery
      )
    },
    unpinnedGroups () {
      const pinnedHookKeys = new Set(this.pinnedGroups.map((g) => g.hookKey))
      return this.groups.filter((g) => !pinnedHookKeys.has(g.hookKey))
    },
    pinnedGroupsHeight () {
      return this.pinnedGroups.length * ROW_HEIGHT + 12
    }
  },
  watch: {
    adToOpen (newValue) {
      if (Object.keys(newValue).length) {
        this.selectedAdvertisement = newValue
        this.showAdvertisementDetailsDrawer = true
      }
    },
    filtersKey () {
      this.fetchAdvertisements()
    },
    sortKey () {
      this.fetchAdvertisements()
    },
    liveStatus () {
      this.fetchPinnedHooks()
    },
    textSearch () {
      this.handleTextSearchUpdate()
    }
  },
  created () {
    // Non-reactive instance counter for stale-response guarding in fetchAdvertisements.
    // Lives outside data() so it doesn't trigger renders.
    this._fetchRequestId = 0
  },
  async mounted () {
    await Promise.all([
      this.fetchAdvertisements(true),
      this.fetchPinnedHooks()
    ])
    this.loadingAdvertisements = false

    this.triggerManualPreviewPlay = () => { this.attemptManualPreviewPlay = !this.attemptManualPreviewPlay }
    window.addEventListener('click', this.triggerManualPreviewPlay)
  },
  beforeDestroy () {
    window.removeEventListener('click', this.triggerManualPreviewPlay)
  },
  methods: {
    ...mapMutations('AdvertisementsModule', ['SET_AD_TO_OPEN']),

    // ==============================================================================================
    // ================================== DATA FETCH METHODS ========================================
    // ==============================================================================================

    async fetchPinnedHooks () {
      try {
        this.loadingPinnedHooks = true
        const opts = { orLiveStatus: this.liveStatus }

        const { results } = await ForeplayAPI.Ads.getBrandPinnedHooks(this.$route.params.id, opts)
        this.pinnedGroups = results
      } catch (error) {
        console.error('Error fetching pinned hooks:', error)
      } finally {
        this.loadingPinnedHooks = false
      }
    },
    async fetchAdvertisements (isInitialFetch = false) {
      // Guard against stale responses when the user rapidly changes filter / sort.
      // Each call increments a request ID; only the latest may mutate state.
      const requestId = ++this._fetchRequestId
      this.loadingAdvertisements = true
      this.isSearch = false
      this.nextPage = null
      
      this.groups = []

      const opts = this.buildFetchOpts()
      opts.excludePinned = true
      if (this.textSearch.length) opts.hookSearch = this.textSearch

      try {
        const { results, nextPage } = await ForeplayAPI.Ads.getBrandHooks(
          this.$route.params.id,
          opts
        )

        // Drop the response if a newer request has started while we awaited.
        if (requestId !== this._fetchRequestId) return
        this.groups = results
        this.nextPage = nextPage ?? null
        // Restart the infinite loader against the fresh result set.
        this.infiniteId += 1
        // Flag if this was a text search
        if (opts.hookSearch) this.isSearch = true
      } catch (e) {
        if (requestId !== this._fetchRequestId) return
        console.error('Error fetching hooks:', e)
        this.groups = []
      } finally {
        if (requestId === this._fetchRequestId && !isInitialFetch) {
          this.loadingAdvertisements = false
        }
      }
    },
    async fetchMore ($state) {
      if (!this.nextPage) {
        $state.complete()
        return
      }
      // Capture the request id so a filter/sort change mid-flight invalidates us.
      const requestId = this._fetchRequestId
      try {
        const opts = this.buildFetchOpts()
        opts.excludePinned = true
        opts.next = this.nextPage
        if (this.textSearch.length) opts.hookSearch = this.textSearch

        const { results, nextPage } = await ForeplayAPI.Ads.getBrandHooks(
          this.$route.params.id,
          opts
        )

        if (requestId !== this._fetchRequestId) return
        if (Array.isArray(results) && results.length) {
          this.groups = this.groups.concat(results)
        }
        this.nextPage = nextPage ?? null
        if (this.nextPage) {
          $state.loaded()
        } else {
          $state.complete()
        }
      } catch (e) {
        console.error('Error fetching more hooks:', e)
        $state.complete()
      }
    },
    // Build the request opts from the active filters/sort. Add filter fields
    // ONLY when actually applied — the endpoint should not receive hardcoded
    // sort / transcriptionOnly / format defaults.
    buildFetchOpts () {
      const opts = {}
      const { activeFilters, selectedDateRange, textSearch } = this.filters || {}
      if (activeFilters && Object.keys(activeFilters).length) Object.assign(opts, activeFilters)

      const startedRunningStart = +new Date(selectedDateRange?.start) || null
      const startedRunningEnd = +new Date(selectedDateRange?.end) || null
      if (startedRunningStart) opts.startedRunningStart = startedRunningStart
      if (startedRunningEnd) opts.startedRunningEnd = startedRunningEnd
      
      if (textSearch) opts.textSearch = textSearch
      if (this.sort && this.sort !== 'longest_running') opts.sort = this.sort
      return opts
    },
    handleTextSearchUpdate: _.debounce(function () {
      this.fetchAdvertisements()
    }, 300),

    // ==============================================================================================
    // ==================================== PINNING METHODS =========================================
    // ==============================================================================================

    async onPin (hookGroup) {
      try {
        const user = this.getUser
        if (!user) return

        await pinHook({
          userId: user.user_id,
          brandId: this.$route.params.id,
          hookKey: hookGroup.hookKey
        })
        this.pinnedGroups = [...this.pinnedGroups, hookGroup]
        this.$showAlert({ type: 'success', message: 'Hook pinned' })
      } catch (error) {
        console.error('Error pinning hook:', error)
        this.$showAlert({ type: 'error', message: 'Failed to pin hook' })
      }
    },
    async onUnpin (hookGroup) {
      try {
        const user = this.getUser
        if (!user) return

        await unpinHook({
          userId: user.user_id,
          brandId: this.$route.params.id,
          hookKey: hookGroup.hookKey
        })
        this.pinnedGroups = this.pinnedGroups.filter((g) => g.hookKey !== hookGroup.hookKey)
        this.$showAlert({ type: 'success', message: 'Hook unpinned' })
      } catch (error) {
        console.error('Error unpinning hook:', error)
        this.$showAlert({ type: 'error', message: 'Failed to unpin hook' })
      }
    },
    async clearPins () {
      try {
        const user = this.getUser
        if (!user) return

        await clearHookPins({
          userId: user.user_id,
          brandId: this.$route.params.id
        })
        this.pinnedGroups = []
        this.$showAlert({ type: 'success', message: 'All pins cleared' })
      } catch (error) {
        console.error('Error clearing pins:', error)
        this.$showAlert({ type: 'error', message: 'Failed to clear pins' })
      }
    },

    // ==============================================================================================
    // ============================ DRAWER/EVENT HANDLER METHODS ====================================
    // ==============================================================================================

    onSeeAds (group) {
      // Always open the multi-ad drawer; it lazy-fetches creatives via /brands/hooks/creatives.
      this.selectedHookGroup = group
      this.showMultiDrawer = true
    },
    onOpenSingleAd (ad) {
      this.selectedAdvertisement = ad
      this.showAdvertisementDetailsDrawer = true
      this.showMultiDrawer = false
      this.selectedHookGroup = null
    },
    onCloseMultiDrawer () {
      this.showMultiDrawer = false
      this.selectedHookGroup = null
    },
    closeModal () {
      this.showAdvertisementDetailsDrawer = false
      this.selectedAdvertisement = {}
      this.SET_AD_TO_OPEN({})
    },
    handleHookThumbnailHover (event) {
      const { elm, media } = event
      if (!elm || !media) {
        this.hookPreview = { video: null, x: 0, y: 0 }
        return
      }
      const elmRect = elm.getBoundingClientRect()
      this.hookPreview = {
        video: media,
        x: elmRect.left + 76,
        y: elmRect.top + (elmRect.height / 2)
      }
    }
  }
}
</script>

<style scoped>
.collapsible-pinned-hooks {
  transition: height 300ms ease-in-out;
  overflow: hidden;
}

.secondary-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  background-color: transparent;
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.secondary-btn:hover {
  box-shadow: none;
  background-color: #F6F8FA;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
